import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import pulseServices from "app/services/pulse-services";
import { SourceChip } from "./LiveTab";
import { ERROR_KIND_LABELS, dateTime, timeAgo } from "./pulseFormat";

const KIND_COLORS = { server: "error", client: "error", rejected: "warning", slow: "warning" };

// Por que no valia la sesion, cuando el usuario sale del token y no de una sesion activa.
// Ver hermes-backend app/services/pulse/session_owner.rb.
const SESSION_LABELS = {
    vencida: "Sesión vencida",
    cerrada: "Sesión cerrada (logout)",
    invalida: "Token inválido",
    sin_validar: "Token sin validar",
};

const ErrorDetail = ({ group, onClose }) => {
    const [rows, setRows] = useState(null);

    useEffect(() => {
        if (!group) return;
        setRows(null);
        pulseServices.errorDetail(group.fingerprint).then(setRows).catch(() => setRows([]));
    }, [group]);

    return (
        <Dialog open={Boolean(group)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{group?.error_class || ERROR_KIND_LABELS[group?.kind]} · {group?.count} veces</DialogTitle>
            <DialogContent dividers>
                {!rows && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress color="warning" /></Stack>}
                {rows && rows.map((e) => (
                    <Box key={e.id} sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                            <Typography variant="body2"><b>{dateTime(e.at)}</b></Typography>
                            <Typography variant="body2" color={e.user_name || e.company_name ? "text.primary" : "text.secondary"}>
                                {e.user_name || e.company_name
                                    ? <>{e.user_name || "Usuario sin identificar"} · <b>{e.company_name || "sin empresa"}</b></>
                                    : "Sin usuario identificado (no vino sesión)"}
                            </Typography>
                            {SESSION_LABELS[e.context?.sesion] && (
                                <Chip size="small" color="warning" variant="outlined" label={SESSION_LABELS[e.context.sesion]} sx={{ height: 20 }} />
                            )}
                            <SourceChip source={e.source} version={e.app_version} />
                            {e.status && <Chip size="small" label={e.status} sx={{ height: 20 }} />}
                        </Stack>
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{e.message}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {[e.http_method && `${e.http_method} ${e.path}`, e.screen && `pantalla ${e.screen}`, e.duration_ms != null && `${e.duration_ms} ms`, e.request_id && `request ${e.request_id}`].filter(Boolean).join(" · ")}
                        </Typography>
                        {e.backtrace && (
                            <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1, fontSize: 11, overflowX: "auto", maxHeight: 220 }}>{e.backtrace}</Box>
                        )}
                        {e.context && Object.keys(e.context).length > 0 && (
                            <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1, fontSize: 11, overflowX: "auto" }}>{JSON.stringify(e.context, null, 2)}</Box>
                        )}
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};

const ErrorsTab = () => {
    const [hours, setHours] = useState(24);
    const [kind, setKind] = useState("");
    const [source, setSource] = useState("");
    const [groups, setGroups] = useState(null);
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        setGroups(null);
        pulseServices.errors({ hours, kind, source }).then(setGroups).catch(() => setGroups([]));
    }, [hours, kind, source]);

    useEffect(() => { load(); }, [load]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>Errores agrupados por causa</Typography>
                    <TextField select size="small" label="Período" value={hours} onChange={(e) => setHours(e.target.value)} sx={{ minWidth: 140 }}>
                        <MenuItem value={1}>Última hora</MenuItem>
                        <MenuItem value={24}>24 horas</MenuItem>
                        <MenuItem value={72}>3 días</MenuItem>
                        <MenuItem value={168}>7 días</MenuItem>
                        <MenuItem value={720}>30 días</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Tipo" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 140 }}>
                        <MenuItem value="">Todos</MenuItem>
                        {Object.entries(ERROR_KIND_LABELS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Origen" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 120 }}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="app">App</MenuItem>
                        <MenuItem value="web">Web</MenuItem>
                        <MenuItem value="api">API</MenuItem>
                        <MenuItem value="system">Sistema</MenuItem>
                    </TextField>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Servidor = excepción en Rails. Cliente = error de JavaScript o crash de la app. Rechazo = el backend dijo que no (400/403/409/422) y el usuario lo vio. Lento = más de 10 s.
                </Typography>
                {!groups ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress color="warning" /></Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="right">Veces</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Qué</TableCell>
                                    <TableCell>Dónde</TableCell>
                                    <TableCell>Clientes</TableCell>
                                    <TableCell align="right">Usuarios</TableCell>
                                    <TableCell>Versiones</TableCell>
                                    <TableCell>Última vez</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map((g) => (
                                    <TableRow key={g.fingerprint} hover sx={{ cursor: "pointer" }} onClick={() => setSelected(g)}>
                                        <TableCell align="right"><b>{g.count}</b></TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <Chip size="small" color={KIND_COLORS[g.kind] || "default"} variant="outlined" label={ERROR_KIND_LABELS[g.kind] || g.kind} sx={{ height: 20 }} />
                                                <SourceChip source={g.source} />
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 420 }}>
                                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                {g.error_class && g.kind !== "rejected" ? <b>{g.error_class}: </b> : null}{g.message}
                                            </Typography>
                                        </TableCell>
                                        <TableCell><Typography variant="caption">{[g.endpoint, g.screen, g.status].filter(Boolean).join(" · ")}</Typography></TableCell>
                                        <TableCell><Typography variant="caption">{g.companies.map((c) => c.name).filter(Boolean).join(", ") || "-"}</Typography></TableCell>
                                        <TableCell align="right">{g.users}</TableCell>
                                        <TableCell><Typography variant="caption">{g.app_versions.join(", ") || "-"}</Typography></TableCell>
                                        <TableCell>{timeAgo(g.last_at)}</TableCell>
                                    </TableRow>
                                ))}
                                {groups.length === 0 && (
                                    <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Sin errores en este período.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
            <ErrorDetail group={selected} onClose={() => setSelected(null)} />
        </Card>
    );
};

export default ErrorsTab;
